<div class="surface-card p-4 sm:p-6">
  <!-- Header -->
  <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
    <div>
      <h2 class="text-text-primary text-xl font-semibold mb-1">{{ 'seekerDashboard.myInstallments.title' | translate }}</h2>
      <p class="text-text-secondary text-sm">{{ 'seekerDashboard.myInstallments.subtitle' | translate }}</p>
    </div>
    <div class="w-12 h-12 flex items-center justify-center rounded-xl bg-surface-elevated text-primary border border-border">
      <i class="fa-solid fa-money-bill-wave text-xl"></i>
    </div>
  </div>

  <!-- Loading state -->
  <div *ngIf="loading" class="rounded-xl border border-border bg-surface shadow-elevated overflow-hidden">
    <div class="border-b border-border-strong bg-surface-elevated px-4 py-4">
      <div class="flex items-center gap-3">
        <span class="spinner"></span>
        <span class="text-sm font-medium text-text-secondary">{{ 'seekerDashboard.myInstallments.subtitle' | translate }}</span>
      </div>
    </div>
    <div class="divide-y divide-border-subtle">
      <div *ngFor="let row of [1, 2, 3, 4]" class="grid grid-cols-5 gap-4 px-4 py-4">
        <div class="skeleton h-4"></div>
        <div class="skeleton h-4"></div>
        <div class="skeleton h-4"></div>
        <div class="skeleton h-6 rounded-full"></div>
        <div class="skeleton h-8 rounded-lg"></div>
      </div>
    </div>
  </div>

  <!-- Error state -->
  <div *ngIf="error && !loading" class="alert alert-danger mx-auto mb-4 max-w-xl rounded-xl shadow-elevated">
    <i class="fa-solid fa-triangle-exclamation mt-0.5"></i>
    <div>
      <p class="font-semibold text-text-primary">Unable to load installments</p>
      <p class="mt-1 text-sm text-text-secondary">{{ error }}</p>
    </div>
  </div>

  <!-- Content -->
  <ng-container *ngIf="!loading && !error && summary">
    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      <div class="rounded-xl border border-success/20 bg-success-soft p-4 shadow-subtle transition-all duration-200 hover:border-success/30">
        <p class="text-sm text-text-secondary font-mono tracking-wider mb-1 uppercase">{{ 'seekerDashboard.myInstallments.totalPaid' | translate }}</p>
        <p class="text-2xl font-bold text-success">{{ summary.totalPaid | number:'1.0-2' }} <span class="text-sm font-normal text-text-muted">EGP</span></p>
        <p class="text-xs text-text-muted mt-2">{{ summary.paidCount }} {{ 'seekerDashboard.myInstallments.installmentsPaid' | translate }}</p>
      </div>
      <div class="rounded-xl border border-warning/20 bg-warning-soft p-4 shadow-subtle transition-all duration-200 hover:border-warning/30">
        <p class="text-sm text-text-secondary font-mono tracking-wider mb-1 uppercase">{{ 'seekerDashboard.myInstallments.totalRemaining' | translate }}</p>
        <p class="text-2xl font-bold text-warning">{{ summary.totalRemaining | number:'1.0-2' }} <span class="text-sm font-normal text-text-muted">EGP</span></p>
        <p class="text-xs text-text-muted mt-2">{{ summary.upcomingCount }} {{ 'seekerDashboard.myInstallments.installmentsRemaining' | translate }}</p>
      </div>
      <div class="rounded-xl border border-danger/20 bg-danger-soft p-4 shadow-subtle transition-all duration-200 hover:border-danger/30">
        <p class="text-sm text-text-secondary font-mono tracking-wider mb-1 uppercase">{{ 'seekerDashboard.myInstallments.overdue' | translate }}</p>
        <p class="text-2xl font-bold text-danger">{{ summary.overdueCount }}</p>
        <p class="text-xs text-text-muted mt-2">{{ 'seekerDashboard.myInstallments.installmentsOverdue' | translate }}</p>
      </div>
      <div class="rounded-xl border border-border-strong bg-surface-elevated p-4 shadow-subtle transition-all duration-200 hover:border-border-primary">
        <p class="text-sm text-text-secondary font-mono tracking-wider mb-1 uppercase">{{ 'seekerDashboard.myInstallments.nextDue' | translate }}</p>
        <p class="text-xl font-bold text-text-primary mb-1">
          <ng-container *ngIf="summary.nextDueAmount > 0; else noDue">
            {{ summary.nextDueAmount | number:'1.0-2' }} <span class="text-sm font-normal text-text-muted">EGP</span>
          </ng-container>
          <ng-template #noDue>-</ng-template>
        </p>
        <p class="text-xs text-text-muted mt-1" *ngIf="summary.nextDueDate">
          <i class="far fa-calendar-alt mr-1"></i> {{ summary.nextDueDate | date:'MMM d, y' }}
        </p>
      </div>
    </div>

    <!-- Installments Table -->
    <div class="rounded-xl border border-border bg-surface shadow-elevated overflow-hidden">
      <div class="flex flex-col gap-3 border-b border-border-strong bg-surface-elevated px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p class="text-sm font-semibold text-text-primary">{{ 'seekerDashboard.myInstallments.title' | translate }}</p>
          <p class="mt-1 text-xs text-text-muted">{{ installments.length }} {{ 'seekerDashboard.myInstallments.subtitle' | translate }}</p>
        </div>
        <div class="inline-flex w-max items-center gap-2 rounded-full border border-border-strong bg-surface px-3 py-1.5 text-xs font-medium text-text-secondary">
          <span class="h-1.5 w-1.5 rounded-full bg-primary shadow-glow"></span>
          {{ summary.totalInstallments }} total
        </div>
      </div>

      <div *ngIf="installments.length > 0" class="hidden overflow-x-auto md:block">
        <table class="table-premium w-full min-w-[760px] text-left text-sm">
          <thead>
            <tr>
              <th class="whitespace-nowrap uppercase tracking-wider">
                <span class="inline-flex items-center gap-2">
                  {{ 'seekerDashboard.myInstallments.number' | translate }}
                  <i class="fa-solid fa-sort text-[10px] text-text-muted"></i>
                </span>
              </th>
              <th class="whitespace-nowrap uppercase tracking-wider">{{ 'seekerDashboard.myInstallments.type' | translate }}</th>
              <th class="whitespace-nowrap uppercase tracking-wider">{{ 'seekerDashboard.myInstallments.amount' | translate }}</th>
              <th class="whitespace-nowrap uppercase tracking-wider">{{ 'seekerDashboard.myInstallments.status' | translate }}</th>
              <th class="text-right whitespace-nowrap uppercase tracking-wider">{{ 'seekerDashboard.myInstallments.actions' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let inst of installments" class="group transition-colors duration-150"
              [ngClass]="inst.status.toLowerCase() === 'overdue' ? 'bg-danger/5' : ''">
              <td class="whitespace-nowrap font-semibold text-text-primary">
                <ng-container *ngIf="inst.installmentNumber === 0; else showNum">
                  -
                </ng-container>
                <ng-template #showNum>
                  #{{ inst.installmentNumber }}
                </ng-template>
              </td>
              <td class="whitespace-nowrap capitalize text-text-secondary">
                {{ 'seekerDashboard.myInstallments.type_' + inst.installmentType.toLowerCase() | translate }}
              </td>
              <td class="whitespace-nowrap">
                <span class="font-semibold tabular-nums text-text-primary">{{ inst.amount | number:'1.0-2' }}</span>
                <span class="ml-1 text-xs font-medium text-text-muted">EGP</span>
              </td>
              <td class="whitespace-nowrap">
                <span class="badge whitespace-nowrap" [ngClass]="getStatusClass(inst.status)">
                  {{ 'seekerDashboard.myInstallments.status_' + inst.status.toLowerCase() | translate }}
                </span>
              </td>
              <td class="whitespace-nowrap">
                <div class="flex items-center justify-end gap-2">
                <button *ngIf="(inst.status === 'pending' || inst.status === 'overdue') && (!inst.payment || inst.payment.status === 'failed')" 
                  (click)="openPaymentModal(inst)"
                  class="btn btn-primary px-3 py-1.5 text-xs">
                  <i class="fa-solid fa-credit-card"></i>
                  <span>{{ 'seekerDashboard.myInstallments.payNow' | translate }}</span>
                </button>
                
                <span *ngIf="inst.payment?.status === 'receipt_uploaded'" 
                  class="badge badge-primary">
                  {{ 'seekerDashboard.myInstallments.underReview' | translate }}
                </span>
                
                <a *ngIf="inst.payment?.receiptImage?.secure_url" 
                  [href]="inst.payment?.receiptImage?.secure_url" 
                  target="_blank" 
                  class="btn-icon" 
                  title="View Receipt">
                  <i class="fas fa-file-invoice text-text-muted hover:text-primary transition-colors"></i>
                </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div *ngIf="installments.length > 0" class="divide-y divide-border-subtle md:hidden">
        <article *ngFor="let inst of installments" class="bg-surface px-4 py-4 transition-colors duration-150 hover:bg-surface-hover"
          [ngClass]="inst.status.toLowerCase() === 'overdue' ? 'border-l-2 border-danger bg-danger/5' : 'border-l-2 border-transparent'">
          <div class="mb-3 flex items-start justify-between gap-3">
            <div>
              <p class="text-sm font-semibold text-text-primary">
                <ng-container *ngIf="inst.installmentNumber === 0; else mobileShowNum">
                  -
                </ng-container>
                <ng-template #mobileShowNum>
                  #{{ inst.installmentNumber }}
                </ng-template>
              </p>
              <p class="mt-1 text-xs capitalize text-text-muted">
                {{ 'seekerDashboard.myInstallments.type_' + inst.installmentType.toLowerCase() | translate }}
              </p>
            </div>
            <span class="badge whitespace-nowrap" [ngClass]="getStatusClass(inst.status)">
              {{ 'seekerDashboard.myInstallments.status_' + inst.status.toLowerCase() | translate }}
            </span>
          </div>

          <div class="mb-4 rounded-lg border border-border-subtle bg-surface-elevated p-3">
            <p class="text-xs uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myInstallments.amount' | translate }}</p>
            <p class="mt-1 text-lg font-bold tabular-nums text-text-primary">
              {{ inst.amount | number:'1.0-2' }} <span class="text-xs font-medium text-text-muted">EGP</span>
            </p>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <button *ngIf="(inst.status === 'pending' || inst.status === 'overdue') && (!inst.payment || inst.payment.status === 'failed')" 
              (click)="openPaymentModal(inst)"
              class="btn btn-primary px-3 py-2 text-xs">
              <i class="fa-solid fa-credit-card"></i>
              <span>{{ 'seekerDashboard.myInstallments.payNow' | translate }}</span>
            </button>
            
            <span *ngIf="inst.payment?.status === 'receipt_uploaded'" 
              class="badge badge-primary">
              {{ 'seekerDashboard.myInstallments.underReview' | translate }}
            </span>
            
            <a *ngIf="inst.payment?.receiptImage?.secure_url" 
              [href]="inst.payment?.receiptImage?.secure_url" 
              target="_blank" 
              class="btn btn-secondary px-3 py-2 text-xs" 
              title="View Receipt">
              <i class="fas fa-file-invoice"></i>
              <span>Receipt</span>
            </a>
          </div>
        </article>
      </div>

      <div *ngIf="installments.length === 0" class="px-4 py-14 text-center">
        <div class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-border-strong bg-surface-elevated text-text-muted">
          <i class="fa-regular fa-folder-open text-xl"></i>
        </div>
        <h3 class="text-base font-semibold text-text-primary">{{ 'seekerDashboard.myInstallments.noInstallments' | translate }}</h3>
        <p class="mx-auto mt-2 max-w-sm text-sm text-text-secondary">{{ 'seekerDashboard.myInstallments.subtitle' | translate }}</p>
      </div>
    </div>
  </ng-container>
</div>

<!-- Payment Modal -->
<div *ngIf="showPaymentModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
  <div class="surface-card w-full max-w-md border border-border-strong shadow-2xl p-6">
    <div class="flex items-center justify-between mb-4 border-b border-border-subtle pb-4">
      <h3 class="text-xl font-bold text-text-primary">Pay Installment #{{ selectedInstallment?.installmentNumber }}</h3>
      <button (click)="closePaymentModal()" class="text-text-muted hover:text-primary transition-colors">
        <i class="fas fa-times text-lg"></i>
      </button>
    </div>

    <!-- Error Message -->
    <div *ngIf="paymentError" class="alert alert-danger p-3 rounded-lg text-sm mb-4">
      {{ paymentError }}
    </div>

    <!-- Success Message -->
    <div *ngIf="paymentSuccess" class="alert alert-success p-3 rounded-lg text-sm mb-4">
      {{ paymentSuccess }}
    </div>

    <form *ngIf="!paymentSuccess" (ngSubmit)="submitPayment()" class="space-y-4">
      
      <!-- Type For Sale -->
      <div>
        <label class="block text-sm font-medium text-text-secondary mb-1">Payment Type</label>
        <select [(ngModel)]="paymentType" name="paymentType" class="form-select w-full px-4 py-2">
          <option value="full">Full Payment</option>
          <option value="installment">Installment Payment</option>
        </select>
      </div>

      <!-- Amount -->
      <div>
        <label class="block text-sm font-medium text-text-secondary mb-1">Amount</label>
        <input type="number" [(ngModel)]="paymentAmount" name="paymentAmount" class="form-input w-full px-4 py-2" />
      </div>

      <!-- Attachment -->
      <div>
        <label class="block text-sm font-medium text-text-secondary mb-1">Receipt Attachment</label>
        <div class="relative w-full border-2 border-dashed border-border-strong rounded-xl p-4 text-center hover:bg-surface-hover transition-colors">
          <input type="file" (change)="onPaymentFileChange($event)" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept="image/*,.pdf" />
          <i class="fas fa-cloud-upload-alt text-text-muted text-2xl mb-2"></i>
          <p class="text-sm text-text-secondary">
            <span class="text-primary font-medium">Click to upload</span> or drag and drop
          </p>
          <p *ngIf="paymentAttachment" class="text-xs text-success font-medium mt-2">
            Selected: {{ paymentAttachment.name }}
          </p>
        </div>
      </div>

      <!-- Submit -->
      <div class="pt-4 flex justify-end gap-3 border-t border-border mt-4">
        <button type="button" (click)="closePaymentModal()" class="btn btn-secondary px-4 py-2 text-sm font-medium">
          Cancel
        </button>
        <button type="submit" [disabled]="paymentSubmitting" class="btn btn-primary px-6 py-2 text-sm font-medium flex items-center gap-2">
          <i class="fas" [ngClass]="paymentSubmitting ? 'fa-spinner fa-spin' : 'fa-check'"></i>
          {{ paymentSubmitting ? 'Submitting...' : 'Confirm Payment' }}
        </button>
      </div>

    </form>
  </div>
</div>
